import { createCheckDate } from "@/utils";
import { Box, Flex, Text } from "@chakra-ui/react";
import { StatusBox } from "@domains/components/statusBox";
import styled from "@emotion/styled";
import { useNewNavigate, useSetCheckInState, useSetCheckOutState, useStatusModeValue } from "@shared/client";
import { BookingStatus } from "@shared/server";
import { FLEX, FONT } from "@styles/common";
import theme from "@styles/theme";

interface DateBoxProps {
    dateNum: string | number;
    result?: BookingStatus;
    endDate?: string;
    childNumber?: number | null;
}

export const DateBox = ({ dateNum, result, endDate, childNumber }: DateBoxProps) => {
    const statusMode = useStatusModeValue();
    const setCheckInState = useSetCheckInState();
    const setCheckOutState = useSetCheckOutState();
    const { onNavigate } = useNewNavigate();

    return (
        <Wrapper nthOfNumber={childNumber as number}>
            <Flex w="100%" justifyContent="space-between" alignItems="center">
                <Text
                    fontWeight={result?.holiday === 1 ? "700" : "500"}
                    color={result?.holiday === 1 ? "#dd107f" : "#333"}
                    size={"16px"}
                >
                    {dateNum}
                </Text>
                {result && <CustomSeasonText>{result.season}</CustomSeasonText>}
            </Flex>
            {result && endDate ? (
                new Date(result.date).getTime() <= new Date(endDate).getTime() ? (
                    <CustomRoomBox>
                        {result.rooms.map((room, index) => {
                            return (
                                <Flex
                                    width={"100%"}
                                    key={index}
                                    alignItems="center"
                                    gap="4px"
                                    justifyContent={statusMode === "price" ? "space-between" : "none"}
                                >
                                    {statusMode === "status" ? (
                                        <>
                                            <StatusBox status={room.status} />
                                            <Box flex={1} whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                                <CustomRoomName
                                                    as="span"
                                                    status={room.status}
                                                    onClick={() => {
                                                        if (room.status === "available") {
                                                            setCheckInState(result.date);
                                                            setCheckOutState(
                                                                createCheckDate(result.date).nextDateString,
                                                            );
                                                            onNavigate({
                                                                path: "/reserve",
                                                                state: { roomIdx: room.roomIdx },
                                                            });
                                                        }
                                                    }}
                                                >
                                                    {room.roomName}
                                                </CustomRoomName>
                                            </Box>
                                        </>
                                    ) : (
                                        <>
                                            <Box whiteSpace={"nowrap"} textOverflow="ellipsis" overflow={"hidden"}>
                                                <CustomRoomName
                                                    status={room.status}
                                                    as="span"
                                                    onClick={() => {
                                                        if (room.status === "available") {
                                                            setCheckInState(result.date);
                                                            setCheckOutState(
                                                                createCheckDate(result.date).nextDateString,
                                                            );
                                                            onNavigate({
                                                                path: "/reserve",
                                                                state: { roomIdx: room.roomIdx },
                                                            });
                                                        }
                                                    }}
                                                >
                                                    {room.roomName}
                                                </CustomRoomName>
                                            </Box>
                                            <Flex textAlign={"right"}>
                                                {room.status === "available" && (
                                                    <Text
                                                        as="span"
                                                        color="black"
                                                        fontSize={"14px"}
                                                        fontWeight={300}
                                                        lineHeight="150%"
                                                        flex={1}
                                                    >
                                                        {room.salePrice.toLocaleString()}
                                                    </Text>
                                                )}
                                            </Flex>
                                        </>
                                    )}
                                </Flex>
                            );
                        })}
                    </CustomRoomBox>
                ) : (
                    <Text fontFamily="Pretendard Variable" fontWeight="500">
                        예약 만료
                    </Text>
                )
            ) : (
                <></>
            )}
        </Wrapper>
    );
};

const Wrapper = styled(Box)<{ nthOfNumber: number }>`
    overflow: hidden;
    ${FLEX.column("flex-start", "flex-start", 16)}
    ${FONT("pretendard", 16, 500, 1)};
    padding: 24px 16px;
    box-sizing: border-box;
    color: ${({ theme }) => theme.COLOR.black};
    border-top: 1px solid ${({ theme }) => theme.COLOR.gray[300]};
    border-right: 1px solid ${({ theme }) => theme.COLOR.gray[300]};

    &:nth-of-type(${({ nthOfNumber }) => nthOfNumber}) {
        border-right: none !important;
    }
`;

const CustomSeasonText = styled(Text)`
    ${FONT("pretendard", 12, 500, 1)}
    color:${({ theme }) => theme.COLOR.gray[400]}
`;

const CustomRoomBox = styled(Box)`
    width: 100%;
    ${FLEX.column("flex-start", "flex-start", 4)}
`;

const CustomRoomName = styled(Text)<{ status: "available" | "disable" | "proceed" | "tel" }>`
    ${FONT("pretendard", 14, 300, 1.5)}
    text-decoration: ${({ status }) => (status === "available" ? "default" : "line-through !important")};
    color: ${({ status }) => (status === "available" ? theme.COLOR.black : theme.COLOR.pink[200])};
    :hover {
        text-decoration: ${({ status }) => (status === "available" ? "underline" : "default !important")};
    }
    cursor: ${({ status }) => (status === "available" ? "pointer" : "default !important")};
`;
